import React, { useEffect, useState } from "react";
import {
  Button,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

type DeferredPrompt = Event & {
  prompt?: () => Promise<void>;
  userChoice?: Promise<{ outcome: string }>;
};

/**
 * PWA install banner – web only.
 *
 * The inner component uses browser-only APIs (window.addEventListener,
 * localStorage) so we gate on Platform.OS before rendering it to avoid
 * crashes on native (Hermes doesn't provide these globals).
 */
export default function PWAInstallPrompt() {
  if (Platform.OS !== "web") return null;
  return <PWAInstallPromptWeb />;
}

function PWAInstallPromptWeb() {
  const [deferredPrompt, setDeferredPrompt] = useState<DeferredPrompt | null>(
    null,
  );
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const DISMISS_KEY = "pwa_install_dismissed_v1";

  useEffect(() => {
    if (Platform.OS !== "web") return;
    try {
      const stored = window.localStorage.getItem(DISMISS_KEY);
      if (stored === "1") setDismissed(true);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;

    function handleBeforeInstallPrompt(e: Event) {
      // Prevent Chrome 67+ from automatically showing the prompt
      // If user already dismissed, don't show again
      try {
        const stored = window.localStorage.getItem(DISMISS_KEY);
        if (stored === "1") return;
      } catch (err) {
        // ignore
      }
      e.preventDefault?.();
      setDeferredPrompt(e as DeferredPrompt);
      setVisible(true);
    }

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener,
    );

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener,
      );
    };
  }, []);

  function onInstallClick() {
    if (!deferredPrompt || !deferredPrompt.prompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice
      ?.then(() => {
        setVisible(false);
        setDeferredPrompt(null);
      })
      .catch(() => {
        setVisible(false);
      });
  }

  function onCloseClick() {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch (err) {
      // ignore
    }
    setDismissed(true);
    setVisible(false);
  }

  // iOS install instructions
  const isIos =
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !(("standalone" in window.navigator) as any);

  if (dismissed) return null;
  if (!visible && !isIos) return null;

  return (
    <View style={styles.container} pointerEvents="box-none">
      {isIos ? (
        <View style={styles.banner}>
          <Text style={styles.text}>
            Install this app: tap Share and then &quot;Add to Home Screen&quot;
          </Text>
          <TouchableOpacity onPress={onCloseClick} accessibilityRole="button">
            <Text style={styles.link}>Close</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.banner}>
          <Text style={styles.text}>
            Install this app for a better experience
          </Text>
          <View style={styles.actions}>
            <Button title="Install" onPress={onInstallClick} />
            <TouchableOpacity onPress={onCloseClick} accessibilityRole="button">
              <Text style={styles.link}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  banner: {
    backgroundColor: "#ffffff",
    padding: 12,
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  },
  actions: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  text: {
    marginBottom: 8,
  },
  link: {
    color: "#007aff",
    marginLeft: 12,
  },
});
